import { parseCpf } from '@brazilian-utils/brazilian-utils';
import type { IntlShape } from 'react-intl';
import { DIGITAL_WALLET_NAMES } from './constants';
import type { PaymentInfo } from './types';

export function formatPaymentMethodName(
  payment: PaymentInfo,
  formatMessage: IntlShape['formatMessage']
): string {
  if (!payment.paymentMethod) {
    return '—';
  }

  if (payment.paymentMethod === 'credit_card') {
    return formatMessage(
      { id: 'paymentInfoDisplay.method.creditCardMasked' },
      { lastDigits: payment.lastDigits }
    );
  }

  if (payment.paymentMethod === 'digital_wallet') {
    if (payment.walletType === 'click_to_pay') {
      const modality = formatMessage({
        id: `paymentInfoDisplay.modality.${payment.transactionType ?? 'credit'}`,
      });
      return formatMessage(
        { id: 'paymentInfoDisplay.method.clickToPay' },
        { modality }
      );
    }

    return (
      DIGITAL_WALLET_NAMES[payment.walletType] ||
      formatMessage({ id: 'paymentInfoDisplay.method.digitalWallet' })
    );
  }

  return formatMessage({
    id: `paymentInfoDisplay.method.${payment.paymentMethod}`,
    defaultMessage: payment.paymentMethod,
  });
}

export function formatInstallment(installments: number): string {
  if (installments === 0) {
    return '—';
  }

  return `${installments}x`;
}

export function formatDocumentMasked(document: string): string {
  if (!document) {
    return '—';
  }

  const cleaned = parseCpf(document);

  if (cleaned.length === 11) {
    const part1 = cleaned.substring(3, 6);
    const part2 = cleaned.substring(6, 9);
    return `•••.${part1}.${part2}-••`;
  }

  if (cleaned.length === 14) {
    const first = cleaned.substring(0, 2);
    const last = cleaned.substring(12, 14);
    return `${first}.•••.•••/••••-${last}`;
  }

  return '—';
}

const LOWERCASE_PREPOSITIONS = ['de', 'da', 'do', 'dos', 'das', 'e'];

export function formatCustomerName(name: string): string {
  if (!name) {
    return '—';
  }

  return name
    .trim()
    .toLowerCase()
    .split(' ')
    .map((word, index) =>
      index > 0 && LOWERCASE_PREPOSITIONS.includes(word)
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');
}
